// receipt.js — one itemized breakdown for every saved payment (owner request
// 2026-10-22): each service with its own price, then subtotal, discount,
// tip, credit card fee and the total. Used by the invoice view/print and by
// every "resend receipt" button, so old and new receipts look the same.
//
// Where the lines come from, best first:
//   metadata.receipt_items — written by the 2026-10-22 backfill for older
//                            payments (description parsed + catalog prices)
//   metadata.cart_items    — saved by the POS at checkout
//   the description        — one line priced at the pre-fee amount

const num = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
const r2 = (v) => Math.round(num(v) * 100) / 100;

function parseMeta(p) {
  const m = p?.metadata;
  if (!m) return {};
  if (typeof m === "string") { try { return JSON.parse(m) || {}; } catch { return {}; } }
  return m;
}
function asArray(v) {
  if (Array.isArray(v)) return v;
  if (typeof v === "string") { try { const a = JSON.parse(v); return Array.isArray(a) ? a : []; } catch { return []; } }
  return [];
}

export function receiptBreakdown(p) {
  const meta = parseMeta(p);
  const total = r2(Math.abs(num(p?.amount)));
  const tip = r2(meta.tip ?? p?.tip);
  const cardFee = r2(meta.card_fee);
  const discount = r2(meta.discount_amount ?? meta.discount);
  const tax = r2(meta.tax);
  const raw = asArray(meta.receipt_items).length ? asArray(meta.receipt_items) : asArray(meta.cart_items);
  let items = raw
    .filter((i) => i && i.name)
    .map((i) => ({ name: String(i.name), qty: Math.max(1, num(i.qty) || 1), price: r2(i.price) }));
  // Pre-discount subtotal. base_amount is what was charged before fee/tip
  // (i.e. after discount).
  let subtotal = r2(meta.subtotal) || (r2(meta.base_amount) ? r2(r2(meta.base_amount) + discount) : 0);
  if (!subtotal) subtotal = r2(total - tip - cardFee - tax + discount);
  if (!items.length) items = [{ name: String(p?.description || "Payment"), qty: 1, price: subtotal }];
  // Lines must add up to what this row actually charged. Split payments
  // (e.g. part Cherry, part card, part cash) store the WHOLE cart on each
  // portion — show that portion as one line at its real amount instead.
  const itemsSum = r2(items.reduce((s, i) => s + i.price * i.qty, 0));
  const adds = r2(subtotal - discount + tip + cardFee + tax);
  if (Math.abs(itemsSum - subtotal) > 0.01 || Math.abs(adds - total) > 0.01) {
    const portion = r2(total - tip - cardFee - tax);
    return { items: [{ name: String(p?.description || "Payment"), qty: 1, price: portion }], subtotal: portion, discount: 0, tip, cardFee, tax, total };
  }
  return { items, subtotal, discount, tip, cardFee, tax, total };
}

// Body for the send-receipt edge function.
export function receiptEmailFields(p) {
  const b = receiptBreakdown(p);
  return {
    amount: b.total, subtotal: b.subtotal, discount: b.discount, tip: b.tip, card_fee: b.cardFee, tax: b.tax,
    items: b.items.map((i) => ({ name: i.name, qty: i.qty, price: i.price })),
  };
}

// HTML rows for the in-app invoice view / print.
export function receiptRowsHtml(p, esc = (s) => String(s).replace(/[<>&]/g, "")) {
  const b = receiptBreakdown(p);
  const money = (v) => "$" + r2(v).toFixed(2);
  const row = (l, v, cls = "") => `<div class="row ${cls}"><span class="label">${esc(l)}</span><span class="val">${v}</span></div>`;
  return [
    ...b.items.map((i) => row(`${i.qty > 1 ? i.qty + "× " : ""}${i.name}`, money(i.price * i.qty), "item")),
    row("Subtotal", money(b.subtotal)),
    b.discount > 0 ? row("Discount", "−" + money(b.discount)) : "",
    b.tax > 0 ? row("Sales tax", money(b.tax)) : "",
    b.tip > 0 ? row("Tip", money(b.tip)) : "",
    b.cardFee > 0 ? row("Credit card processing fee", money(b.cardFee)) : "",
  ].join("");
}
